import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SpeedIcon from '@mui/icons-material/Speed';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Can from '../../components/Can';
import remitoVentaService, { RemitoVenta } from '../../services/remitoVentaService';
import ventasSeguimientoService, {
  VentaPendienteMaster,
  formatComprobanteVentaMasterDisplay,
} from '../../services/ventasSeguimientoService';
import { ROUTES, facturaPosVerPath } from '../../config/routes';
import PDFViewer from '../../components/shared/PDFViewer';
import DocumentosKpisBar from '../../components/shared/DocumentosKpisBar';
import { DocumentosKpis, emptyDocumentosKpis } from '../../utils/documentosKpis';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatClienteConNro } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';

type DocumentoPendienteEntrega = {
  nroFormularioMaster: number;
  nroTipoFormularioMaster?: string | number | null;
  nroCliente?: number;
  nombreCliente?: string;
  comprobante: string;
  fechaEmision?: string | null;
  renglonesConPendiente: number;
  totalPendienteEntregar: number;
};

const aggregateDocumentosPendienteEntrega = (items: VentaPendienteMaster[]): DocumentoPendienteEntrega[] => {
  const conEntrega = items.filter((i) => Number(i.pendienteEntregar) > 0);
  const map = new Map<number, DocumentoPendienteEntrega>();

  for (const p of conEntrega) {
    const key = p.nroFormularioMaster;
    const pend = Number(p.pendienteEntregar) || 0;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, {
        nroFormularioMaster: key,
        nroTipoFormularioMaster: p.nroTipoFormularioMaster,
        nroCliente: p.nroCliente,
        nombreCliente: p.nombreCliente,
        comprobante: formatComprobanteVentaMasterDisplay(p),
        fechaEmision: p.fechaEmisionComprobanteMaster,
        renglonesConPendiente: 1,
        totalPendienteEntregar: pend,
      });
    } else {
      prev.renglonesConPendiente += 1;
      prev.totalPendienteEntregar += pend;
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const fa = a.fechaEmision ? new Date(a.fechaEmision).getTime() : 0;
    const fb = b.fechaEmision ? new Date(b.fechaEmision).getTime() : 0;
    return fb - fa;
  });
};

const formatCantidad = (n: number) =>
  new Intl.NumberFormat('es-AR', { maximumFractionDigits: 4, minimumFractionDigits: 0 }).format(n);

const Remitos: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [remitos, setRemitos] = useState<RemitoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedRemito, setSelectedRemito] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [remitoDetalle, setRemitoDetalle] = useState<RemitoVenta | null>(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [pendientesDialogOpen, setPendientesDialogOpen] = useState(false);
  const [pendientesLoading, setPendientesLoading] = useState(false);
  const [documentosPendientesEntrega, setDocumentosPendientesEntrega] = useState<DocumentoPendienteEntrega[]>([]);
  const [kpis, setKpis] = useState<DocumentosKpis>(emptyDocumentosKpis);

  const fetchRemitos = async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    nroSuc: number | null
  ) => {
    try {
      setLoading(true);
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await remitoVentaService.getRemitosVenta({
        pageNumber: pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde || undefined,
        fechaHasta: hasta || undefined,
        nroSuc: resolveNroSucFiltro(nroSuc),
      });
      setRemitos(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: unknown) {
      console.error('Error al cargar remitos:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar los remitos'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      setRemitos([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filtroListo) return;
    const timeoutId = setTimeout(() => {
      fetchRemitos(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo]);

  useEffect(() => {
    if (!filtroListo) return;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const result = await remitoVentaService.getRemitosVentaKpis({
          searchTerm: searchTerm || undefined,
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          nroSuc: resolveNroSucFiltro(filtroNroSuc),
        });
        if (!cancelled) setKpis(result);
      } catch (error: unknown) {
        if (cancelled) return;
        setKpis(emptyDocumentosKpis());
        enqueueSnackbar(getApiErrorMessage(error, 'Error al calcular los KPI de remitos'), {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      setPdfLoading(nroFormulario);
      const response = await api.get(`/api/RemitosVenta/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as Record<string, string>)?.['content-disposition'];
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName ?? `Remito_${nroFormulario}.pdf`);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedRemito(nroFormulario);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al generar el PDF'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedRemito(null);
    setPdfFileName(null);
  };

  const handleVerDetalle = async (nroFormulario: number) => {
    setLoadingDetalle(true);
    setViewDialogOpen(true);
    setRemitoDetalle(null);
    try {
      const detalle = await remitoVentaService.getRemitoVenta(nroFormulario);
      setRemitoDetalle(detalle);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el remito'), {
        variant: 'error',
      });
      setViewDialogOpen(false);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setRemitoDetalle(null);
  };

  const handleAbrirDocumentoMaster = (doc: DocumentoPendienteEntrega) => {
    const tipo = String(doc.nroTipoFormularioMaster ?? '').trim();
    const id = doc.nroFormularioMaster;
    if (tipo === '12') {
      navigate(`/ventas/pedidos/${id}/ver`);
      setPendientesDialogOpen(false);
      return;
    }
    if (tipo === '10') {
      navigate(`/ventas/cotizaciones/${id}/ver`);
      setPendientesDialogOpen(false);
      return;
    }
    if (tipo === '1' || tipo === '2') {
      navigate(facturaPosVerPath(id));
      setPendientesDialogOpen(false);
      return;
    }
    enqueueSnackbar(
      'No hay pantalla de detalle para este tipo de comprobante. Consulte el documento por su número en el módulo correspondiente.',
      { variant: 'info', autoHideDuration: 6000 }
    );
  };

  const _handleBuscarPendientesEntrega = async () => {
    setPendientesDialogOpen(true);
    setPendientesLoading(true);
    setDocumentosPendientesEntrega([]);
    try {
      const items = await ventasSeguimientoService.getPendientesMaster(undefined, true);
      setDocumentosPendientesEntrega(aggregateDocumentosPendienteEntrega(items));
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al buscar documentos con entrega pendiente'), {
        variant: 'error',
        autoHideDuration: 6000,
      });
      setPendientesDialogOpen(false);
    } finally {
      setPendientesLoading(false);
    }
  };

  const formatDate = (dateVal?: string | Date) => {
    if (!dateVal) return '-';
    try {
      const date = dateVal instanceof Date ? dateVal : new Date(dateVal);
      return date.toLocaleDateString('es-AR');
    } catch {
      return '-';
    }
  };

  const formatComprobante = (remito: RemitoVenta) => {
    const { letra, nroSuc, nroComprobante } = remito;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return remito.nroRemito ?? '-';
    }
    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();
    return letraNormalizada ? `${letraNormalizada}-${sucursal}-${comprobante}` : (remito.nroRemito ?? '-');
  };

  const handleComprobanteClick = (remito: RemitoVenta) => {
    if (!(remito.nroFormulario > 0)) return;
    void handleVerDetalle(remito.nroFormulario);
  };

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              Remitos de Venta
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Can permiso="ventas.remitos.nuevo">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={() => navigate(ROUTES.ventas.remitos.pos)}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nuevo Remito POS
              </Button>
              </Can>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, comprobante, cliente, nombre..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Hasta"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          <DocumentosKpisBar
            cantidad={kpis.cantidad || totalRecords}
            cantidadLabel="remitos"
            precioTotal={kpis.precioTotal}
            precioNeto={kpis.precioNeto}
            pesoTotalKg={kpis.pesoTotalKg}
            volumenTotalM3={kpis.volumenTotalM3}
          />

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {remitos.map((remito) => {
                const comprobante = formatComprobante(remito);
                return (
                  <Card
                    key={remito.nroFormulario}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              component="span"
                              variant="subtitle1"
                              sx={{ ...comprobanteSx, fontWeight: 600, display: 'block' }}
                              onClick={() => handleComprobanteClick(remito)}
                            >
                              {comprobante !== '-' ? comprobante : 'Remito'}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(remito.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {formatClienteConNro(remito.nroCliente, remito.nombre)}
                        </Typography>
                        {remito.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {remito.usuario}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          <Tooltip title="Visualizar">
                            <IconButton
                              onClick={() => handleVerDetalle(remito.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                transition: 'all 0.2s',
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(remito.nroFormulario)}
                              size="small"
                              disabled={pdfLoading === remito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                '&:disabled': { bgcolor: '#ccc' },
                                transition: 'all 0.2s',
                              }}
                            >
                              {pdfLoading === remito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && remitos.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay remitos para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%',
                },
              }}
            >
              <Table
                size={isTablet ? 'small' : 'medium'}
                sx={{
                  minWidth: 800,
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' },
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                      Fecha
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>
                      Comprobante
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', xl: 'table-cell' }, minWidth: 80 }}>
                      Usuario
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center" width={120}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {remitos.map((remito) => (
                    <TableRow
                      key={remito.nroFormulario}
                      sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                    >
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {formatDate(remito.fechaEmision)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                        <Typography
                          component="span"
                          sx={comprobanteSx}
                          onClick={() => handleComprobanteClick(remito)}
                        >
                          {formatComprobante(remito)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 280,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                        }}
                      >
                        {formatClienteConNro(remito.nroCliente, remito.nombre)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                        {remito.usuario ?? '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                          <Tooltip title="Visualizar">
                            <IconButton
                              onClick={() => handleVerDetalle(remito.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                transition: 'all 0.2s',
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(remito.nroFormulario)}
                              size="small"
                              disabled={pdfLoading === remito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                '&:disabled': { bgcolor: '#ccc' },
                                transition: 'all 0.2s',
                              }}
                            >
                              {pdfLoading === remito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {loading && remitos.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && remitos.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay remitos para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                  },
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedRemito && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Remito ${selectedRemito}`}
          documentId={String(selectedRemito)}
          documentType="remito"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      <Dialog open={pendientesDialogOpen} onClose={() => setPendientesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 600 }}>Documentos con entrega pendiente</DialogTitle>
        <DialogContent dividers>
          {pendientesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!pendientesLoading && documentosPendientesEntrega.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No hay documentos con cantidad pendiente de entregar.
            </Typography>
          )}
          {!pendientesLoading && documentosPendientesEntrega.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Documento</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2', display: { xs: 'none', sm: 'table-cell' } }}>
                    Cliente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">
                    Renglones
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">
                    Pend. entregar
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="center" width={100}>
                    Abrir
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentosPendientesEntrega.map((doc) => (
                  <TableRow key={doc.nroFormularioMaster} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {doc.comprobante}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {doc.nroCliente != null ? `${doc.nroCliente} — ` : ''}
                        {doc.nombreCliente?.trim() || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {doc.nroCliente != null ? `${doc.nroCliente} — ` : ''}
                      {doc.nombreCliente?.trim() || '-'}
                    </TableCell>
                    <TableCell align="right">{doc.renglonesConPendiente}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {formatCantidad(doc.totalPendienteEntregar)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Abrir documento">
                        <IconButton
                          size="small"
                          onClick={() => handleAbrirDocumentoMaster(doc)}
                          sx={{ color: '#1976d2' }}
                          aria-label="Abrir documento"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendientesDialogOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 600 }}>Detalle del Remito</DialogTitle>
        <DialogContent>
          {loadingDetalle && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loadingDetalle && remitoDetalle && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography>
                <strong>Nro. Remito:</strong> {remitoDetalle.nroRemito ?? '-'}
              </Typography>
              <Typography>
                <strong>Fecha:</strong> {formatDate(remitoDetalle.fechaEmision)}
              </Typography>
              <Typography>
                <strong>Cliente:</strong> {formatClienteConNro(remitoDetalle.nroCliente, remitoDetalle.nombre)}
              </Typography>
              {remitoDetalle.descripcion && (
                <Typography>
                  <strong>Descripción:</strong> {remitoDetalle.descripcion}
                </Typography>
              )}
              {remitoDetalle.detalles && remitoDetalle.detalles.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Items:
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {remitoDetalle.detalles.map((d) => (
                        <TableRow key={d.nroItem}>
                          <TableCell>{d.codigoArticulo}</TableCell>
                          <TableCell>{d.descripcionArticulo}</TableCell>
                          <TableCell align="right">{d.cantidad}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              <NumeroInternoMeta nro={remitoDetalle.nroFormulario} />
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Remitos;
